import { Button } from "@ondit/ui";
import { CircleInfo, TriangleAlert } from "@ondit/ui/icons";
import type { ReactNode } from "react";
import { balanceAction } from "~/common/billing/balance";
import { useAccess } from "~/core/access";
import type { Me, Org, OrgEntity } from "~/core/api";
import { useT } from "~/core/i18n";
import { WorkspaceChrome } from "~/shell/chrome";
import { readCollapsedCookie } from "~/shell/chrome/collapse";
import { SearchPalette, SearchProvider } from "~/shell/search";
import { Sidebar } from "~/shell/sidebar";

/**
 * The shell every signed-in screen sits in, both modules included.
 *
 * It was two frames until 2026-09-12 — this one for Analytics and the Agent's
 * `WorkspaceChrome` for the Agent — mounted as siblings of the same `<Routes>`,
 * so crossing between the modules tore one down and built the other: the left
 * column remounted, and its scroll position, its open sections and its hover
 * state went with it. That is the seam the merge was for. `WorkspaceChrome` is
 * the frame now, for both, and it lives in `~/shell` because it is the
 * application's rather than a module's.
 *
 * What is still the module's is the middle of the column. `navigation` is
 * Analytics' five stages or the Agent's chats and workflows, and nothing else
 * about the column changes when a reader crosses between them.
 */
export function AppShell({
	me,
	org,
	brands,
	brandId,
	canAddBrand,
	onAddBrand,
	onOpenSettings,
	onOpenBrandSettings,
	navigation,
	pane,
	banners,
	children,
}: {
	me: Me;
	org: Org;
	brands: OrgEntity[];
	brandId?: string;
	canAddBrand: boolean;
	onAddBrand: () => void;
	onOpenSettings?: () => void;
	onOpenBrandSettings?: () => void;
	/** The active module's navigation. */
	navigation: ReactNode;
	/**
	 * Whether the screen sizes itself against the pane or flows inside it.
	 *
	 * Not the same question as which module's navigation is drawn, and keying
	 * both off one answer put Settings — a form, opened from the Agent — into a
	 * pane with no padding. The Agent's own screens are canvases and editors;
	 * everything else, its module included, is a document.
	 */
	pane: "padded" | "bare";
	/**
	 * What is true of the whole account, above the pane rather than inside it:
	 * the Agent's screens size themselves against the pane, so a notice drawn
	 * inside one pushed its scroll container off the bottom and the Agent went
	 * without them.
	 */
	banners?: ReactNode;
	children: ReactNode;
}) {
	const t = useT();
	return (
		<SearchProvider>
			<div className="layout">
				<a href="#main-content" className="skip-link">
					{t("nav.skipContent")}
				</a>
				<WorkspaceChrome
					initialSidebarCollapsed={readCollapsedCookie()}
					sidebar={
						<Sidebar
							me={me}
							org={org}
							brands={brands}
							brandId={brandId}
							canAddBrand={canAddBrand}
							onAddBrand={onAddBrand}
							onOpenSettings={onOpenSettings}
							onOpenBrandSettings={onOpenBrandSettings}
						>
							{navigation}
						</Sidebar>
					}
				>
					<div className="pane-stack">
						{banners}
						<main id="main-content" className="content" data-pane={pane} tabIndex={-1}>
							{children}
						</main>
					</div>
				</WorkspaceChrome>
				<SearchPalette />
			</div>
		</SearchProvider>
	);
}

/** How long before the trial ends the column starts saying so. */
const TRIAL_NOTICE_DAYS = 7;

/**
 * The balance below which the number stops being information and starts being
 * a warning. A single reading costs more than this, so an account here is one
 * click away from a refusal — which is the moment to sell the top-up, rather
 * than after the click.
 */
const LOW_CREDITS = 100;

/** Whole days from now, or null where there is no date. Negative reads as 0. */
function daysUntil(when: string | null | undefined): number | null {
	if (!when) return null;
	const ms = new Date(when).getTime() - Date.now();
	return Number.isNaN(ms) ? null : Math.max(0, Math.ceil(ms / 86_400_000));
}

/**
 * What is true of the whole account, said once at the top.
 *
 * Rather than by disabling thirteen screens' worth of buttons: a viewer who
 * presses one is refused by the route with the same sentence, and a person who
 * cannot find out *why* the screen refuses them is the failure worth
 * preventing.
 */
export function Banners({
	org,
	brand,
	onOpenPlan,
	onTopUp,
}: {
	org: Org;
	brand: OrgEntity;
	onOpenPlan: () => void;
	onTopUp: () => void;
}) {
	const t = useT();
	const { admin } = useAccess();
	const banners: ReactNode[] = [];

	if (brand.role === "viewer")
		banners.push(
			<div className="banner banner-note" key="viewer">
				<CircleInfo className="banner-icon" aria-hidden />
				<span>{t("banner.viewer", { brand: brand.name })}</span>
			</div>,
		);

	// Above the pause: a lapsed plan is why nothing runs, and the way out of it
	// is a different button.
	if (org.subscription_state === "expired")
		banners.push(
			<div className="banner banner-danger" key="expired">
				<TriangleAlert className="banner-icon" aria-hidden />
				<span>{t("banner.expired")}</span>
				{/* Renewing is the account's money, so a member is told whose button
				    it is rather than shown one that refuses them. */}
				{admin ? (
					<Button className="spacer" type="button" onClick={onOpenPlan}>
						{t("banner.renew")}
					</Button>
				) : (
					<span className="spacer">{t("banner.renewAsk")}</span>
				)}
			</div>,
		);

	// The trial's last days, on every screen rather than only on the plan page:
	// it does not renew, so the day it ends is the day the account stops
	// measuring, and somebody reading a chart is who needs to know.
	const trialDaysLeft = daysUntil(org.subscription_ends_at);
	if (org.trial && org.subscription_state === "active" && trialDaysLeft !== null && trialDaysLeft <= TRIAL_NOTICE_DAYS)
		banners.push(
			<div className="banner banner-warn" key="trial">
				<TriangleAlert className="banner-icon" aria-hidden />
				<span>{t(trialDaysLeft <= 0 ? "banner.trialToday" : "banner.trialEnding", { n: String(trialDaysLeft) })}</span>
				{admin ? (
					<Button className="spacer" type="button" onClick={onOpenPlan}>
						{t("banner.trialUpgrade")}
					</Button>
				) : (
					<span className="spacer">{t("banner.renewAsk")}</span>
				)}
			</div>,
		);

	// Not while the plan is lapsed: renewing is the louder thing to fix and it is
	// already said above, and a pack cannot be sold to a lapsed plan anyway.
	if (org.credits < LOW_CREDITS && org.subscription_state !== "expired") {
		const action = balanceAction(org, admin);
		banners.push(
			<div className="banner banner-warn" key="credits">
				<TriangleAlert className="banner-icon" aria-hidden />
				<span>{t("banner.lowCredits", { n: String(org.credits) })}</span>
				{action === null ? (
					<span className="spacer">{t("banner.topUpAsk")}</span>
				) : (
					<Button className="spacer" type="button" onClick={action === "top-up" ? onTopUp : onOpenPlan}>
						{t(action === "top-up" ? "topup.open" : "banner.trialUpgrade")}
					</Button>
				)}
			</div>,
		);
	}

	if (org.paused)
		banners.push(
			<div className="banner banner-warn" key="paused">
				<TriangleAlert className="banner-icon" aria-hidden />
				<span>{t("banner.paused")}</span>
			</div>,
		);

	return banners.length ? (
		<div className="account-banners" role="status">
			{banners}
		</div>
	) : null;
}
